/**
 * `/tournaments` — list of bench tournament archives from
 * `/replays/index.json`.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Badge, Button } from "@pilot-protocol/ui";
import { loadBenchIndex, type BenchIndexEntry } from "../data/bench";

export default function TournamentsPage() {
  const [tournaments, setTournaments] = useState<
    ReadonlyArray<BenchIndexEntry> | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const index = await loadBenchIndex();
        if (!cancelled) setTournaments(index.tournaments);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={pageStyle}>
      <header style={headStyle}>
        <Badge variant="outline">Tournaments</Badge>
        <h1 style={titleStyle}>Curated runs</h1>
        <p style={leadStyle}>
          Round-robins against the public model set. Drill in to see every
          match, every turn, every line of reasoning.
        </p>
      </header>

      {error ? (
        <p style={errorStyle}>Failed to load: {error}</p>
      ) : tournaments === null ? (
        <p style={leadStyle}>Loading…</p>
      ) : (
        <ul style={listStyle}>
          {tournaments.map((t) => (
            <li key={t.id} style={rowStyle}>
              <div style={rowMainStyle}>
                <p style={rowNameStyle}>{t.name}</p>
                {t.description ? (
                  <p style={rowDescStyle}>{t.description}</p>
                ) : null}
                <p style={rowMetaStyle}>
                  {t.agents.length} agents
                  {t.startedAtUnixMs
                    ? ` · ${new Date(t.startedAtUnixMs).toLocaleDateString()}`
                    : ""}
                </p>
              </div>
              <Link to={`/tournament/${t.id}`} style={{ textDecoration: "none" }}>
                <Button variant="outline" size="sm">
                  Open ›
                </Button>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const pageStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 24,
};

const headStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  alignItems: "flex-start",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-display)",
  fontSize: "var(--text-3xl)",
};

const leadStyle: CSSProperties = {
  margin: 0,
  maxWidth: 640,
  color: "var(--foreground-secondary)",
  fontSize: "var(--text-base)",
  lineHeight: 1.5,
};

const listStyle: CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "12px 16px",
  border: "1px solid var(--pp-border)",
  background: "var(--pp-bg-strong)",
};

const rowMainStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
};

const rowNameStyle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-display)",
  fontSize: "var(--text-lg)",
  color: "var(--pp-text)",
};

const rowDescStyle: CSSProperties = {
  margin: 0,
  color: "var(--foreground-secondary)",
  fontSize: "var(--text-sm)",
};

const rowMetaStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--text-xs)",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--foreground-muted)",
};

const errorStyle: CSSProperties = {
  margin: 0,
  color: "var(--destructive)",
};
